'use client';

import { Button, Card, Flex, IconButton, Popover, Portal, Stack, Text } from '@chakra-ui/react';
import EditDeleteActions from '@components/common/EditDeleteActions';
import ExpandableSearchButton from '@components/common/ExpandableSearchButton';
import EditCommitmentDialog from '@features/positions/supplyCommitments/EditCommitmentDialog';
import { formatDate } from 'helpers/dates';
import { useHref } from 'hooks/useHref';
import { memo, useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  LuBuilding2,
  LuCalendarClock,
  LuChevronRight,
  LuCircleCheck,
  LuPackagePlus,
  LuPackageX,
  LuRadio,
  LuUndo2,
  LuWorkflow,
} from 'react-icons/lu';
import { FilterIcon, PlusIcon } from 'vectors';

export interface SupplyCommitmentEntry {
  id: string;
  itemName: string;
  quantity: number;
  unit: string;
  price: number;
  currency: string;
  deliveryDate: string;
  partnerName: string | null;
  createdAt: string;
  status: 'active' | 'pending' | 'fulfilled';
  automationCount: number;
}

const fakeCommitments: SupplyCommitmentEntry[] = [
  {
    id: 'sc-1',
    itemName: 'Organic Wheat Flour',
    quantity: 3000,
    unit: 'kg',
    price: 2.5,
    currency: 'GHS',
    deliveryDate: '2026-04-15',
    partnerName: 'Harvest Foods Co.',
    createdAt: '2026-03-20T08:00:00Z',
    status: 'active',
    automationCount: 1,
  },
  {
    id: 'sc-2',
    itemName: 'Cold-Pressed Olive Oil',
    quantity: 800,
    unit: 'liters',
    price: 12.0,
    currency: 'GHS',
    deliveryDate: '2026-04-10',
    partnerName: 'Harvest Foods Co.',
    createdAt: '2026-03-19T14:00:00Z',
    status: 'active',
    automationCount: 0,
  },
  {
    id: 'sc-3',
    itemName: 'Solar Panel Modules',
    quantity: 120,
    unit: 'units',
    price: 450.0,
    currency: 'GHS',
    deliveryDate: '2026-05-01',
    partnerName: null,
    createdAt: '2026-03-22T08:00:00Z',
    status: 'pending',
    automationCount: 2,
  },
  {
    id: 'sc-4',
    itemName: 'Portland Cement',
    quantity: 15000,
    unit: 'kg',
    price: 0.35,
    currency: 'GHS',
    deliveryDate: '2026-04-14',
    partnerName: 'CoreMat Supply',
    createdAt: '2026-03-18T08:00:00Z',
    status: 'fulfilled',
    automationCount: 0,
  },
  {
    id: 'sc-5',
    itemName: 'Copper Wire (12 AWG)',
    quantity: 3000,
    unit: 'meters',
    price: 4.2,
    currency: 'GHS',
    deliveryDate: '2026-04-20',
    partnerName: 'CoreMat Supply',
    createdAt: '2026-03-20T09:00:00Z',
    status: 'active',
    automationCount: 1,
  },
];

const WORKFLOW_TRIGGER_DEADLINE = { trigger: 'delivery_deadline_approaching' };
const WORKFLOW_TRIGGER_WITHDRAWN = { trigger: 'commitment_withdrawn' };
const WORKFLOW_PICKER_TRIGGERS = { picker: 'triggers' };
const WORKFLOW_VIEW_LIST = { view: 'list' };

const statusConfig: Record<string, { label: string; color: string }> = {
  active: { label: 'Active', color: 'green.600' },
  pending: { label: 'Pending', color: 'orange.600' },
  fulfilled: { label: 'Fulfilled', color: 'blue.600' },
};

interface CommitmentCardProps {
  commitment: SupplyCommitmentEntry;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onWalkBack: (id: string) => void;
  onMarkFulfilled: (id: string) => void;
  onNavigateWorkflow: (params?: Record<string, string>) => void;
}

const CommitmentCard = memo(
  ({ commitment, onEdit, onDelete, onWalkBack, onMarkFulfilled, onNavigateWorkflow }: CommitmentCardProps) => {
    const config = statusConfig[commitment.status];
    const canDelete = commitment.status === 'pending' || commitment.partnerName == null;
    const canAct = commitment.status === 'active';

    const onClickWalkBack = useCallback(() => {
      onWalkBack(commitment.id);
    }, [onWalkBack, commitment.id]);

    const onClickMarkFulfilled = useCallback(() => {
      onMarkFulfilled(commitment.id);
    }, [onMarkFulfilled, commitment.id]);

    const onClickDeadline = useCallback(() => {
      onNavigateWorkflow(WORKFLOW_TRIGGER_DEADLINE);
    }, [onNavigateWorkflow]);

    const onClickWithdrawn = useCallback(() => {
      onNavigateWorkflow(WORKFLOW_TRIGGER_WITHDRAWN);
    }, [onNavigateWorkflow]);

    const onClickCreate = useCallback(() => {
      onNavigateWorkflow(WORKFLOW_PICKER_TRIGGERS);
    }, [onNavigateWorkflow]);

    const onClickViewAutomations = useCallback(() => {
      onNavigateWorkflow(WORKFLOW_VIEW_LIST);
    }, [onNavigateWorkflow]);

    const hasAutomations = commitment.automationCount > 0;

    return (
      <Card.Root w="full" borderColor="border.light" borderRadius="xl" bg="surface.background">
        <Card.Body p={0} gap={3}>
          <Flex direction="row" alignItems="center" gap={2} px={5} pt={5}>
            <Flex
              bg="surface.hover"
              borderWidth="1px"
              borderColor="border.light"
              borderRadius="md"
              px={2}
              py={0.5}
            >
              <Text fontSize="xs" fontWeight="semibold" color={config.color} letterSpacing="wide">
                {config.label.toUpperCase()}
              </Text>
            </Flex>
            <Flex direction="row" alignItems="center" gap={1.5}>
              {commitment.partnerName != null ? (
                <>
                  <Text fontSize="sm" color="fg.subtle">
                    Supply to
                  </Text>
                  <LuBuilding2 size={14} />
                  <Text fontSize="sm" fontWeight="medium">
                    {commitment.partnerName}
                  </Text>
                </>
              ) : (
                <>
                  <LuRadio size={14} />
                  <Text fontSize="sm" fontWeight="medium" fontStyle="italic">
                    Open signal
                  </Text>
                </>
              )}
            </Flex>
            <Flex ml="auto">
              <EditDeleteActions
                id={commitment.id}
                onEdit={onEdit}
                onDelete={canDelete ? onDelete : undefined}
              />
            </Flex>
          </Flex>
          <Flex flexDir="column" gap={3} px={5}>
            <Flex direction="row" alignItems="center" gap={2}>
              <LuPackagePlus size={18} />
              <Text fontSize="lg" fontWeight="medium">
                {commitment.itemName} — {commitment.quantity.toLocaleString()} {commitment.unit} @{' '}
                {commitment.currency} {commitment.price}
              </Text>
            </Flex>
            <Stack
              direction={{ base: 'column', md: 'row' }}
              gap={3}
              alignItems={{ base: 'flex-start', md: 'center' }}
              fontWeight="semibold"
            >
              <Flex direction="row" alignItems="center" gap={2}>
                <Text fontSize="xs" color="text.secondary">
                  Delivery by
                </Text>
                <Text fontSize="sm">{formatDate(commitment.deliveryDate)}</Text>
              </Flex>
              <Flex direction="row" alignItems="center" gap={2}>
                <Text fontSize="xs" color="text.secondary">
                  Created
                </Text>
                <Text fontSize="sm">{formatDate(commitment.createdAt)}</Text>
              </Flex>
            </Stack>
          </Flex>
          <Flex
            borderTopWidth="1px"
            borderColor="border.light"
            px={5}
            py={2.5}
            gap={2}
            justifyContent="flex-end"
          >
            <Popover.Root positioning={{ placement: 'bottom-end' }}>
              <Popover.Trigger asChild>
                <Button size="xs" variant="subtle" borderRadius="md">
                  <LuWorkflow size={14} />
                  Automate{hasAutomations ? ` · ${commitment.automationCount}` : ''}
                </Button>
              </Popover.Trigger>
              <Portal>
                <Popover.Positioner>
                  <Popover.Content w="auto" minW="250px" p={1} borderRadius="lg">
                    <Flex direction="column" gap={0.5}>
                      <Button
                        variant="ghost"
                        size="sm"
                        borderRadius="md"
                        justifyContent="flex-start"
                        onClick={onClickDeadline}
                      >
                        <LuCalendarClock size={16} />
                        <Text fontSize="sm">Delivery deadline approaching</Text>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        borderRadius="md"
                        justifyContent="flex-start"
                        onClick={onClickWithdrawn}
                      >
                        <LuPackageX size={16} />
                        <Text fontSize="sm">Commitment withdrawn</Text>
                      </Button>
                      <Flex borderTopWidth="1px" borderColor="border.light" mt={0.5} pt={0.5} direction="column" gap={0.5}>
                        <Button
                          variant="ghost"
                          size="sm"
                          borderRadius="md"
                          justifyContent="flex-start"
                          w="full"
                          onClick={onClickCreate}
                        >
                          <LuChevronRight size={14} />
                          <Text fontSize="sm" color="text.secondary">
                            Create new
                          </Text>
                        </Button>
                        {hasAutomations && (
                          <Button
                            variant="ghost"
                            size="sm"
                            borderRadius="md"
                            justifyContent="flex-start"
                            w="full"
                            onClick={onClickViewAutomations}
                          >
                            <LuWorkflow size={14} />
                            <Text fontSize="sm" color="text.secondary">
                              View automations ({commitment.automationCount})
                            </Text>
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                  </Popover.Content>
                </Popover.Positioner>
              </Portal>
            </Popover.Root>
            {canAct && (
              <Button
                size="xs"
                variant="subtle"
                colorPalette="green"
                borderRadius="md"
                onClick={onClickMarkFulfilled}
              >
                <LuCircleCheck size={14} />
                Mark fulfilled
              </Button>
            )}
            {canAct && (
              <Button
                size="xs"
                variant="subtle"
                colorPalette="orange"
                borderRadius="md"
                onClick={onClickWalkBack}
              >
                <LuUndo2 size={14} />
                Withdraw
              </Button>
            )}
          </Flex>
        </Card.Body>
      </Card.Root>
    );
  },
);
CommitmentCard.displayName = 'CommitmentCard';

const MySupplyCommitmentsPage = () => {
  const router = useRouter();
  const { supplyHref, workflowsHref } = useHref();
  const [searchQuery, setSearchQuery] = useState('');
  const [editingCommitment, setEditingCommitment] = useState<SupplyCommitmentEntry | null>(null);

  const onClickNewCommitment = useCallback(() => {
    router.push(supplyHref());
  }, [supplyHref, router]);

  const onEditCommitment = useCallback(
    (id: string) => {
      const commitment = fakeCommitments.find((c) => c.id === id) ?? null;
      setEditingCommitment(commitment);
    },
    [],
  );

  const onCloseEditDialog = useCallback(() => {
    setEditingCommitment(null);
  }, []);

  const onDeleteCommitment = useCallback((_id: string) => {}, []);

  const onWalkBackCommitment = useCallback((_id: string) => {}, []);

  const onMarkFulfilled = useCallback((_id: string) => {}, []);

  const onNavigateWorkflow = useCallback(
    (params?: Record<string, string>) => {
      const href = workflowsHref(params);
      router.push(href);
    },
    [workflowsHref, router],
  );

  const filteredCommitments = useMemo(() => {
    if (searchQuery === '') {
      return fakeCommitments;
    }
    const q = searchQuery.toLowerCase();
    return fakeCommitments.filter(
      (c) =>
        c.itemName.toLowerCase().includes(q) ||
        (c.partnerName != null && c.partnerName.toLowerCase().includes(q)),
    );
  }, [searchQuery]);

  return (
    <Flex flexDirection="column" h="full" p={4}>
      <Flex borderBottomWidth="1px" borderColor="border.light" pb={3} alignItems="center">
        <Flex ml="auto" gap={2} alignItems="center">
          <ExpandableSearchButton
            query={searchQuery}
            setQuery={setSearchQuery}
            size="xs"
            iconColor="text.secondary"
          />
          <IconButton variant="ghost" size="xs" borderRadius="md">
            <FilterIcon color="text.secondary" />
          </IconButton>
          <Button size="2xs" borderRadius="md" onClick={onClickNewCommitment}>
            New supply <PlusIcon />
          </Button>
        </Flex>
      </Flex>
      <Stack gap={6} pt={4}>
        {filteredCommitments.map((commitment) => (
          <CommitmentCard
            key={commitment.id}
            commitment={commitment}
            onEdit={onEditCommitment}
            onDelete={onDeleteCommitment}
            onWalkBack={onWalkBackCommitment}
            onMarkFulfilled={onMarkFulfilled}
            onNavigateWorkflow={onNavigateWorkflow}
          />
        ))}
      </Stack>
      <EditCommitmentDialog commitment={editingCommitment} onClose={onCloseEditDialog} />
    </Flex>
  );
};

export default memo(MySupplyCommitmentsPage);
